// ==============================|| PREFERENCES DATABASE SYNC ||============================== //

import axios from 'utils/axios';

/**
 * Utility functions for syncing preferences with database
 */

/**
 * Load user preferences from database on login
 * @returns {Promise<object>} User preferences from database
 */
export const loadPreferencesFromDB = async () => {
  try {
    const response = await axios.get('/server/api/user_preferences');
    if (response?.data?.success && response?.data?.data?.preferences) {
      // Preferences is now returned as an object directly, not a JSON string
      return response.data.data.preferences;
    }
    return {};
  } catch (error) {
    console.error('Error loading preferences from database:', error);
    return {};
  }
};

/**
 * Save preferences to database
 * @param {object} preferences - Preferences to save
 * @returns {Promise<boolean>} Success status
 */
export const savePreferencesToDB = async (preferences) => {
  try {
    // RESTful pattern: send preferences as object directly, not JSON string
    const response = await axios.post('/server/api/user_preferences', {
      preferences: preferences
    });
    return response?.data?.success || false;
  } catch (error) {
    console.error('Error saving preferences to database:', error);
    return false;
  }
};

/**
 * Sync all preferences to database
 * @returns {Promise<boolean>} Success status
 */
export const syncAllPreferencesToDB = async () => {
  try {
    // Get current sessionStorage preferences
    const currentPrefs = JSON.parse(sessionStorage.getItem('prefs') || '{}');
    
    // Create a copy for syncing, excluding session-only preferences
    const prefsToSync = { ...currentPrefs };
    
    // Remove Client preference from Divisions module (session-only)
    if (prefsToSync.Divisions && prefsToSync.Divisions.Client) {
      const { Client, ...divisionsPrefs } = prefsToSync.Divisions;
      prefsToSync.Divisions = divisionsPrefs;
    }
    
    // Save filtered preferences to database
    const success = await savePreferencesToDB(prefsToSync);
    
    return success;
  } catch (error) {
    console.error('Error syncing preferences to database:', error);
    return false;
  }
};

/**
 * Initialize preferences on login
 * Loads from database and populates sessionStorage
 * @returns {Promise<object>} Loaded preferences
 */
export const initializePreferences = async () => {
  try {
    // Load from database
    const dbPreferences = await loadPreferencesFromDB();
    
    // Store in sessionStorage for fast access
    sessionStorage.setItem('prefs', JSON.stringify(dbPreferences));

    // Apply font size to <html> element immediately if Global preferences exist
    if (dbPreferences.Global && dbPreferences.Global.FontSize) {
      document.documentElement.style.fontSize = `${dbPreferences.Global.FontSize}px`;
    }

    window.dispatchEvent(new CustomEvent('preferencesLoaded', { detail: dbPreferences }));

    return dbPreferences;
  } catch (error) {
    console.error('Error initializing preferences:', error);
    return {};
  }
};

/**
 * Clear session preferences on logout.
 * Profile preferences remain in the database for the next login.
 */
export const clearPreferences = async () => {
  try {
    sessionStorage.removeItem('prefs');
  } catch (error) {
    console.error('Error clearing preferences:', error);
  }
};

export default {
  loadPreferencesFromDB,
  savePreferencesToDB,
  syncAllPreferencesToDB,
  initializePreferences,
  clearPreferences
};
